/**
 * Writing direction of a COMPLETE user/LLM message, decided by MAJORITY script.
 *
 * The web counterpart of iOS `messageIsRTL` / Android `messageIsRtl` (and the RN app's
 * `detectTextDirection`) — a deliberate 1:1 mirror: same stripping pre-pass, same RTL scalar ranges,
 * same "≥50% of the remaining letters" rule. Change one, change all three.
 *
 * Why not `dir="auto"`: the browser resolves it by FIRST STRONG CHARACTER, so a mostly-Hebrew line that
 * opens with a Latin word — a product name, a name like "Ronny", an English word the speaker
 * code-switched to — lays out with an LTR paragraph base. The Hebrew runs still render correctly
 * internally, so the only visible symptom is trailing neutrals (a "!" or "?") landing on the wrong side.
 * That is exactly the bug this exists to prevent, and it is why `composerDir` (first strong char, which
 * is CORRECT for a composer following what is being typed) must not be used for a settled message.
 *
 * LTR is "any Unicode letter that isn't RTL" via `\p{L}` rather than an ASCII/Latin check, so
 * Cyrillic/Greek/CJK count as letters instead of falling through to the empty-text fallback.
 */

const RTL_CHAR = /[֐-׿؀-ۿݐ-ݿࢠ-ࣿיִ-﷿ﹰ-﻿]/;
const LETTER_CHAR = /\p{L}/u;

/**
 * Parts that must not sway the verdict — fenced/inline code, markdown link targets (visible text kept),
 * bare URLs/deep links, markdown syntax characters and digits. Mirrors the native `dirStripSteps` so all
 * three surfaces count the same "meaningful" letters.
 */
const STRIP_STEPS: [RegExp, string][] = [
  [/```[\s\S]*?```/g, ' '],
  [/`[^`]*`/g, ' '],
  [/\[([^\]]*)\]\([^)]*\)/g, '$1'],
  [/\b[a-z][\w+.-]*:\/\/\S+/gi, ' '],
  [/[#*_~>`|=+\-[\]()]/g, ' '],
  [/[0-9]/g, ' '],
];

/** True when the settled text should lay out right-to-left. */
export function messageIsRtl(text: string): boolean {
  let stripped = text;
  for (const [pattern, replacement] of STRIP_STEPS) stripped = stripped.replace(pattern, replacement);
  let rtl = 0;
  let ltr = 0;
  for (const ch of stripped) {
    if (RTL_CHAR.test(ch)) rtl += 1;
    else if (LETTER_CHAR.test(ch)) ltr += 1;
  }
  const total = rtl + ltr;
  return total > 0 && rtl / total >= 0.5;
}

/** The same verdict as an HTML `dir` attribute value. */
export function messageDir(text: string): 'rtl' | 'ltr' {
  return messageIsRtl(text) ? 'rtl' : 'ltr';
}
